import Swal from "sweetalert2";
import { toast } from "@zerodevx/svelte-toast";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import {
    completeMessageRestore,
    completeRedoRestore,
    downloadAssistantCheckpoint,
    prepareMessageRestore,
    prepareRedoRestore,
} from "@app/store/assistantStore.svelte.js";
import * as m from "@app/paraglide/messages.js";

/** Plain-text explanation of what a restore changes (notes §3.5). */
function describeRestore(prepared) {
    const { summary, skippedLockedGroups, detached } = prepared;
    const parts = [];
    if (summary.unchanged) {
        parts.push(m.assistant_restore_nothing());
    } else {
        if (summary.groups.length) {
            parts.push(m.assistant_restore_summary({ events: summary.events, groups: summary.groups.join(", ") }));
        }
        if (summary.projectChanged) {
            parts.push(m.assistant_restore_project_changed());
        }
        parts.push(m.assistant_restore_manual_edits());
    }
    if (skippedLockedGroups.length) {
        parts.push(m.assistant_restore_locked({ groups: skippedLockedGroups.join(", ") }));
    }
    if (detached) {
        parts.push(m.assistant_restore_detached());
    }
    parts.push(m.assistant_restore_undo_hint());
    return parts.join(" ");
}

/**
 * @returns {Promise<"restore"|"download"|null>}
 */
async function askToRestore(prepared, { title, confirmText }) {
    const result = await Swal.fire({
        ...SWAL_BASE,
        customClass: { ...SWAL_CLASSES.confirmCancel, denyButton: "btn btn-outline-secondary me-2" },
        title,
        text: describeRestore(prepared),
        icon: prepared.detached ? "warning" : "question",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: confirmText,
        denyButtonText: m.assistant_restore_download(),
        cancelButtonText: m.cancel(),
    });
    if (result.isConfirmed) return "restore";
    if (result.isDenied) return "download";
    return null;
}

async function download(checkpointId) {
    try {
        const ok = await downloadAssistantCheckpoint(checkpointId, m.assistant_restore_copy_suffix());
        if (!ok) toast.push(m.assistant_checkpoint_expired(), { classes: ["toast-warning"] });
    } catch (error) {
        console.error("Checkpoint download failed", error);
        toast.push(m.assistant_restore_failed({ message: error.message }), { classes: ["toast-danger"] });
    }
}

async function run(task) {
    try {
        if (await task()) {
            toast.push(m.assistant_restore_done(), { classes: ["toast-success"] });
        }
    } catch (error) {
        console.error("Checkpoint restore failed", error);
        toast.push(m.assistant_restore_failed({ message: error.message }), { classes: ["toast-danger"] });
    }
}

async function prepareOrWarn(prepare) {
    let prepared;
    try {
        prepared = await prepare();
    } catch (error) {
        console.error("Checkpoint load failed", error);
        toast.push(m.assistant_restore_failed({ message: error.message }), { classes: ["toast-danger"] });
        return null;
    }
    if (prepared?.expired) {
        toast.push(m.assistant_checkpoint_expired(), { classes: ["toast-warning"] });
        return null;
    }
    return prepared;
}

/**
 * Restore to the checkpoint before a prompt, after confirmation.
 * @param {string} messageId
 * @param {{ edit?: boolean }} [options]  edit: also rewind the chat and refill the prompt
 */
export async function confirmRestoreToMessage(messageId, { edit = false } = {}) {
    const prepared = await prepareOrWarn(() => prepareMessageRestore(messageId));
    if (!prepared) return;
    const choice = await askToRestore(prepared, {
        title: edit ? m.assistant_edit_restore_title() : m.assistant_restore_title(),
        confirmText: edit ? m.assistant_edit_restore_button() : m.assistant_restore_button(),
    });
    if (choice === "download") return download(prepared.checkpointId);
    if (choice === "restore") return run(() => completeMessageRestore(messageId, prepared, { edit }));
}

/** Undo a restore from its marker in the chat, after confirmation. */
export async function confirmRedoRestore(eventId) {
    const prepared = await prepareOrWarn(() => prepareRedoRestore(eventId));
    if (!prepared) return;
    const choice = await askToRestore(prepared, {
        title: m.assistant_redo_restore_title(),
        confirmText: m.assistant_redo_restore_button(),
    });
    if (choice === "download") return download(prepared.checkpointId);
    if (choice === "restore") return run(() => completeRedoRestore(eventId, prepared));
}
